// src/shared/api/interceptors/auth.ts
import { type InternalAxiosRequestConfig } from 'axios';
import { axiosInstance } from '../client';
import { tokenService } from '@/shared/lib/token/tokenService';
import {
  setTokenGetter,
  getTokenGetter,
  whenTokenGetterReady,
} from './tokenGetter';

export { setTokenGetter as setAuth0TokenGetter };

/**
 * Attach the bearer token to every request.
 *
 * Registered through an exported function rather than at module scope: a
 * top-level `axiosInstance.interceptors.use(...)` is only reachable via a bare
 * `import './auth'`, which a bundler is free to drop once the package declares
 * `sideEffects`. That is exactly what happened — the interceptors vanished from
 * the production build while the dev server (no tree-shaking) looked fine.
 */
export function registerAuthInterceptor(): void {
  axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Wait briefly if Auth0's getter has not been registered yet, instead of
    // sending the request bare. Without this the first call of a cold load
    // raced the provider tree and 401'd — see whenTokenGetterReady.
    const getter = getTokenGetter() ?? (await whenTokenGetterReady());
    if (getter) {
      try {
        const accessToken = await getter();
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
          tokenService.setAccessToken(accessToken);
        }
      } catch {
        // Silent failure — request proceeds without token
      }
    }
    return config;
  },
    (error) => Promise.reject(error),
  );
}
